import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { Destinatary } from '@/types/DestinataryType';
import { brebSignerLookupService } from '@/shared/breb/keys/services/brebSignerLookup.service';
import { resolveBrebAliasValue } from '@/shared/breb/utils/brebRoutingCode.util';
import type { SignerLookupResponse } from '@/shared/breb/keys/types/brebSignerLookup.type';
import { resolveSignerDisplayName } from '@/shared/breb/keys/utils/brebSignerName.util';

interface UseBrebSigner {
  brebSigner: Ref<SignerLookupResponse | null>;
  fetchBrebSigner: () => Promise<void>;
  beneficiarySignerName: ComputedRef<string>;
  beneficiaryBank: ComputedRef<string>;
  beneficiaryAccountNumber: ComputedRef<string>;
}

/**
 * Resuelve los datos del firmante Bre-B (titular real de la llave) del
 * destinatario vía lookup, para mostrar el banco destino y el número de cuenta
 * del envío Bre-B en lugar del contacto guardado (BB-258). Opera solo sobre el
 * contexto de envío; nunca evalúa la feature flag.
 * @param selectedContact - Destinatario seleccionado; el alias a consultar sale de `routingCodeValue2`, con fallback a `routingCodeValue1`.
 * @param isBrebSendContext - Contexto de envío Bre-B (método de pago Bre-B).
 * @returns Ref del firmante, disparador del lookup y los datos derivados: nombre del titular, banco y número de cuenta.
 */
export function useBrebSigner(
  selectedContact: Ref<Destinatary>,
  isBrebSendContext: ComputedRef<boolean>
): UseBrebSigner {
  const brebSigner = ref<SignerLookupResponse | null>(null);

  /**
   * Consulta el lookup del firmante y guarda el resultado en `brebSigner`; no-op
   * fuera de contexto Bre-B o sin alias, y deja `brebSigner` en null si falla.
   */
  async function fetchBrebSigner(): Promise<void> {
    if (!isBrebSendContext.value) return;

    const aliasValue = resolveBrebAliasValue(selectedContact.value);
    if (!aliasValue) return;

    const result = await brebSignerLookupService.getSignerLookup({ aliasValue });
    brebSigner.value = result.isRight() ? result.value.data : null;
  }

  const beneficiarySignerName = computed(() => {
    if (!isBrebSendContext.value) return '';

    const signer = brebSigner.value;
    if (!signer) return '';

    return resolveSignerDisplayName(signer);
  });

  const beneficiaryBank = computed(() =>
    isBrebSendContext.value ? brebSigner.value?.bankName ?? '' : ''
  );

  const beneficiaryAccountNumber = computed(() =>
    isBrebSendContext.value ? brebSigner.value?.bankAccountNumber ?? '' : ''
  );

  return {
    brebSigner,
    fetchBrebSigner,
    beneficiarySignerName,
    beneficiaryBank,
    beneficiaryAccountNumber,
  };
}
